import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaTelegramPlane, FaWhatsapp, FaGlobe, FaCopy, FaCheck, FaExternalLinkAlt, FaTimes } from 'react-icons/fa';
import WebChatLook from './WebChatLook';
import VoiceChannel from './VoiceChannel';
import BillingStatus from './BillingStatus';
import WhatsappTemplates from './WhatsappTemplates';
import { signupConfigured, missingSignupVars, startEmbeddedSignup, preloadSignup, SignupCancelled } from '../../../services/whatsappSignup';
import { webChatLink, webChatSnippet } from '../../../services/engineService';
import Btn from '../../../components/ui/Btn';
import StatusTag from '../../../components/ui/StatusTag';
import ConfirmModal from '../../../components/ui/ConfirmModal';
import Field, { TextInput } from '../../../components/ui/Field';

const ACTIVE = ['connected', 'degraded'];
const when = (iso) => (iso ? new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : 'todavía no');

function ConnectionRow({ conn, label, onDisconnect }) {
  const [confirm, setConfirm] = useState(false);
  const live = ACTIVE.includes(conn.status);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3 border-t border-line first:border-0">
      <span className="text-sm text-fg">{label}</span>
      {live ? <StatusTag tone="live" dot>Conectado</StatusTag> : conn.status === 'error' ? <StatusTag tone="caution">Con error</StatusTag> : <StatusTag>Desconectado</StatusTag>}
      <span className="text-[12.5px] text-fg-faint">Último mensaje: {when(conn.last_event_at)}</span>
      {live && <Btn size="sm" variant="ghost" className="ml-auto" onClick={() => setConfirm(true)}>Desconectar</Btn>}
      {conn.last_error && <p className="basis-full text-[12.5px] text-caution">{conn.last_error}</p>}
      <ConfirmModal open={confirm} onClose={() => setConfirm(false)} title={`Desconectar ${label}`} confirmLabel="Desconectar" tone="danger" onConfirm={() => onDisconnect(conn.id)}>
        <p>El agente dejará de responder por este canal en cuanto confirmes. Puedes volver a conectarlo después.</p>
      </ConfirmModal>
    </div>
  );
}

function Card({ icon: Icon, title, description, children, muted }) {
  return (
    <section className={`rounded-xl border p-5 ${muted ? 'border-dashed border-fg/15' : 'border-line bg-panel/50'}`} aria-label={title}>
      <div className="flex items-start gap-3">
        <span className={`grid place-items-center h-9 w-9 rounded-lg shrink-0 ${muted ? 'bg-raised text-fg-faint' : 'bg-raised text-fg'}`}><Icon aria-hidden /></span>
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-semibold text-fg">{title}</h3>
          <p className="text-[13px] text-fg-dim mt-0.5">{description}</p>
          <div className="mt-4">{children}</div>
        </div>
      </div>
    </section>
  );
}

function CopyButton({ text, label }) {
  const [done, setDone] = useState(false);
  useEffect(() => { if (!done) return undefined; const t = setTimeout(() => setDone(false), 1600); return () => clearTimeout(t); }, [done]);
  return (
    <Btn size="sm" variant="ghost" onClick={() => { navigator.clipboard?.writeText(text); setDone(true); }} aria-label={label}>
      {done ? <FaCheck aria-hidden /> : <FaCopy aria-hidden />}<span>{done ? 'Copiado' : 'Copiar'}</span>
    </Btn>
  );
}

/** Sitios donde se puede pegar el widget: se agregan y quitan uno por uno y se guardan al momento. */
function OriginsEditor({ origins, onSave, disabled }) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async (list) => {
    setBusy(true); setError('');
    try { await onSave(list); setValue(''); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const add = () => {
    let v = value.trim().replace(/\/+$/, '');
    if (!v) return;
    if (!/^https?:\/\//i.test(v)) v = `https://${v}`;
    if (origins.includes(v.toLowerCase())) { setValue(''); return; }
    save([...origins, v]);
  };
  return (
    <div>
      {origins.length > 0 && (
        <ul className="flex flex-wrap gap-2 mb-3" aria-label="Sitios permitidos">
          {origins.map((o) => (
            <li key={o} className="inline-flex items-center gap-1.5 rounded-full border border-line bg-raised pl-3 pr-1 py-1 text-[13px] text-fg">
              {o.replace(/^https?:\/\//, '')}
              <button type="button" disabled={disabled || busy} onClick={() => save(origins.filter((x) => x !== o))} aria-label={`Quitar ${o}`}
                className="h-6 w-6 grid place-items-center rounded-full text-fg-faint hover:text-fg hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60"><FaTimes aria-hidden className="text-[11px]" /></button>
            </li>
          ))}
        </ul>
      )}
      <form className="flex flex-col sm:flex-row gap-3 sm:items-start" onSubmit={(e) => { e.preventDefault(); add(); }}>
        <Field label="Agregar un sitio" className="flex-1" error={error} hint="El dominio donde vas a pegar el código, por ejemplo www.cliente.mx">
          <TextInput value={value} onChange={(e) => setValue(e.target.value)} placeholder="www.cliente.mx" spellCheck={false} disabled={disabled} />
        </Field>
        <Btn type="submit" className="sm:mt-[26px] shrink-0" disabled={!value.trim() || disabled} loading={busy}>Agregar sitio</Btn>
      </form>
    </div>
  );
}

function WebCard({ conn, agent, available, readOnly, engineReady, onOrigins, onSaveLook }) {
  const key = conn?.public_key;
  const origins = conn?.external?.origins || [];
  return (
    <section className="rounded-xl border border-live/25 bg-panel/60 p-5" aria-label="Chat web">
      <div className="flex flex-wrap items-start gap-3">
        <span className="grid place-items-center h-9 w-9 rounded-lg shrink-0 bg-live-soft text-live"><FaGlobe aria-hidden /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[15px] font-semibold text-fg">Chat web</h3>
            <StatusTag tone="live" dot>Siempre encendido</StatusTag>
          </div>
          <p className="text-[13px] text-fg-dim mt-0.5">Todos los agentes lo tienen. Se prueba con un enlace y se pega en cualquier sitio con una línea de código.</p>
        </div>
      </div>

      {!key ? (
        <p className="mt-4 text-[13px] text-fg-faint" aria-busy="true">{engineReady ? 'Preparando el chat web…' : 'Se activa cuando el admin tenga la dirección del motor (REACT_APP_ENGINE_URL).'}</p>
      ) : (
        <div className="mt-5 space-y-6">
          {!available && (
            <p role="note" className="rounded-lg border border-caution/30 bg-caution-soft px-4 py-3 text-[13px] text-fg">
              El chat ya existe, pero responde hasta que el agente esté <b>publicado</b> y el <b>motor encendido</b>. Mientras, el enlace muestra "No disponible por ahora" y en los sitios no aparece.
            </p>
          )}
          <div>
            <h4 className="text-[13px] font-medium text-fg">Probar o compartir</h4>
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-line bg-ink px-3 py-2">
              <code className="text-[12.5px] text-live break-all flex-1 min-w-0">{webChatLink(key)}</code>
              <CopyButton text={webChatLink(key)} label="Copiar el enlace del chat" />
              <a href={webChatLink(key)} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[13px] text-fg hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">
                <FaExternalLinkAlt aria-hidden className="text-[11px]" />Abrir
              </a>
            </div>
          </div>
          <div>
            <h4 className="text-[13px] font-medium text-fg">Pegar en un sitio</h4>
            <p className="text-[12.5px] text-fg-faint mt-0.5">Antes de <code>&lt;/body&gt;</code>. Aparece abajo a la derecha, solo en los sitios de la lista.</p>
            <div className="mt-2 flex flex-wrap items-start gap-2 rounded-lg border border-line bg-ink px-3 py-2">
              <code className="text-[12.5px] text-fg break-all flex-1 min-w-0">{webChatSnippet(key)}</code>
              <CopyButton text={webChatSnippet(key)} label="Copiar el código del widget" />
            </div>
          </div>
          <div>
            <h4 className="text-[13px] font-medium text-fg mb-2">Sitios donde puede aparecer</h4>
            {origins.length === 0 && <p className="text-[12.5px] text-fg-faint mb-3">Ninguno todavía: por ahora solo funciona en el enlace de arriba.</p>}
            <OriginsEditor origins={origins} onSave={onOrigins} disabled={!engineReady || readOnly} />
          </div>
        </div>
      )}
    </section>
  );
}

/** Sección «Chat web»: enlace, código para pegar, sitios permitidos y apariencia. */
export function WebChatPanel({ agent, available, readOnly, connections, engineReady, onWebOrigins, onSaveLook }) {
  const web = connections.find((c) => c.channel === 'web');
  return (
    <div className="space-y-4">
      <WebCard conn={web} agent={agent} available={available} readOnly={readOnly} engineReady={engineReady} onOrigins={onWebOrigins} onSaveLook={onSaveLook} />
      {web?.public_key && (
        <section className="rounded-xl border border-line bg-panel/50 p-5 sm:p-6" aria-label="Apariencia del chat web">
          <h3 className="font-display text-[19px] font-bold text-fg">Apariencia</h3>
          <p className="text-[13px] text-fg-dim mt-0.5 mb-6 max-w-[70ch]">Colores, tema, avatar, textos y posición. Se ve al momento en el enlace y en los sitios donde esté pegado, sin publicar el agente.</p>
          <WebChatLook agentName={agent.name} stored={web.external?.look} readOnly={readOnly} onSave={onSaveLook} />
        </section>
      )}
    </div>
  );
}

// Canales: el chat web va primero (su detalle vive en su propia sección); Telegram y WhatsApp se conectan aquí con la API del motor.
export default function ChannelsPanel({ readOnly, connections, engineReady, webTo, onTelegram, onWhatsapp, onDisconnect }) {
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState({});
  const tg = connections.filter((c) => c.channel === 'telegram');
  const wa = connections.filter((c) => c.channel === 'whatsapp');
  const web = connections.find((c) => c.channel === 'web');
  const [waStep, setWaStep] = useState('');       // '' | 'ventana' | 'conectando'
  const [waConfirm, setWaConfirm] = useState(false);
  useEffect(() => { preloadSignup(); }, []);

  const run = async (key, fn) => {
    setBusy(key); setError((x) => ({ ...x, [key]: '' }));
    try { return await fn(); } catch (e) { setError((x) => ({ ...x, [key]: e.message })); return null; } finally { setBusy(''); }
  };
  // Registro integrado: ventana de Meta → código + cuenta y número → el motor lo verifica con Meta, registra el número y suscribe la app.
  const connectWa = async () => {
    setError((x) => ({ ...x, wa: '' })); setWaStep('ventana');
    try {
      const r = await startEmbeddedSignup();
      setWaStep('conectando');
      await onWhatsapp(r);
    } catch (e) {
      if (!(e instanceof SignupCancelled)) setError((x) => ({ ...x, wa: e.message }));
    } finally { setWaStep(''); }
  };
  const connectTg = () => run('tg', async () => { await onTelegram(token.trim()); setToken(''); });

  return (
    <div className="space-y-4">
      {!engineReady && (
        <p role="note" className="rounded-lg border border-caution/30 bg-caution-soft px-4 py-3 text-[13px] text-fg">
          Falta configurar la dirección del motor (<code>REACT_APP_ENGINE_URL</code>) en el admin: sin ella no se pueden conectar canales.
        </p>
      )}

      <section className="rounded-xl border border-live/25 bg-panel/60 p-5 flex flex-wrap items-center gap-3" aria-label="Chat web">
        <span className="grid place-items-center h-9 w-9 rounded-lg shrink-0 bg-live-soft text-live"><FaGlobe aria-hidden /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[15px] font-semibold text-fg">Chat web</h3>
            <StatusTag tone="live" dot>Siempre encendido</StatusTag>
          </div>
          <p className="text-[13px] text-fg-dim mt-0.5">{web?.external?.origins?.length ? `En ${web.external.origins.length === 1 ? '1 sitio' : `${web.external.origins.length} sitios`} y en su enlace.` : 'Todos los agentes lo tienen: se prueba con un enlace y se pega en cualquier sitio.'}</p>
        </div>
        <Link to={webTo} className="inline-flex items-center h-9 px-4 rounded-full text-[13.5px] font-semibold text-fg bg-raised ring-1 ring-line hover:ring-fg/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">Código, sitios y apariencia</Link>
      </section>

      <Card icon={FaTelegramPlane} title="Telegram" description="Un bot de Telegram responde con este agente.">
        {tg.map((c) => <ConnectionRow key={c.id} conn={c} label={c.external?.username ? `@${c.external.username}` : 'Bot de Telegram'} onDisconnect={onDisconnect} />)}
        {!tg.some((c) => ACTIVE.includes(c.status)) && (
          <div className={`${tg.length ? 'mt-3 pt-4 border-t border-line' : ''} flex flex-col sm:flex-row gap-3 sm:items-start`}>
            <Field label="Token del bot" className="flex-1" error={error.tg} hint="Créalo en Telegram con @BotFather (/newbot) y pega aquí el token que te da.">
              <TextInput type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="123456789:AA…" autoComplete="off" spellCheck={false} />
            </Field>
            <Btn variant="primary" className="sm:mt-[26px] shrink-0" onClick={connectTg} disabled={!token.trim() || !engineReady} loading={busy === 'tg'}>Conectar Telegram</Btn>
          </div>
        )}
      </Card>

      <Card icon={FaWhatsapp} title="WhatsApp" description="El número del cliente responde con este agente. Se conecta con su propia cuenta de Meta, sin pasar contraseñas por aquí.">
        {wa.map((c) => (
          <React.Fragment key={c.id}>
            <ConnectionRow conn={c} label={[c.external?.display_phone_number, c.external?.verified_name].filter(Boolean).join(' · ') || 'Número de WhatsApp'} onDisconnect={onDisconnect} />
            {ACTIVE.includes(c.status) && <BillingStatus conn={c} engineReady={engineReady} />}
            {ACTIVE.includes(c.status) && <VoiceChannel conn={c} readOnly={readOnly} engineReady={engineReady} />}
            {ACTIVE.includes(c.status) && <WhatsappTemplates conn={c} readOnly={readOnly} engineReady={engineReady} />}
          </React.Fragment>
        ))}
        {!signupConfigured ? (
          <p role="note" className="text-[13px] text-fg-faint">
            Para activarlo falta configurar {missingSignupVars.map((v, i) => <React.Fragment key={v}>{i > 0 && ' y '}<code>{v}</code></React.Fragment>)} en el .env del admin (reinicia <code>npm start</code> después). Los números que hoy atiende n8n siguen igual.
          </p>
        ) : (
          <div className={`${wa.length ? 'mt-3 pt-4 border-t border-line' : ''} flex flex-col gap-3`}>
            <div className="flex flex-wrap items-center gap-3">
              <Btn variant="primary" onClick={() => setWaConfirm(true)} disabled={!engineReady || readOnly || Boolean(waStep)} loading={Boolean(waStep)}>
                {waStep === 'ventana' ? 'Esperando a Meta…' : waStep === 'conectando' ? 'Conectando el número…' : wa.some((c) => ACTIVE.includes(c.status)) ? 'Conectar otro número' : 'Conectar WhatsApp'}
              </Btn>
              {waStep === 'ventana' && <span role="status" className="text-[12.5px] text-fg-faint">Termina el proceso en la ventana de Meta; si no la ves, revisa que el navegador no bloquee ventanas emergentes.</span>}
            </div>
            {error.wa && <p role="alert" className="text-[13px] text-signal">{error.wa}</p>}
          </div>
        )}
      </Card>

      <ConfirmModal open={waConfirm} onClose={() => setWaConfirm(false)} title="Conectar un número de WhatsApp" confirmLabel="Abrir la ventana de Meta" onConfirm={() => { connectWa(); }}>
        <p>Se abre una ventana de Meta para que el cliente elija o cree su cuenta de WhatsApp Business y su número, y lo verifique si hace falta.</p>
        <p><b>Cuidado:</b> el número se registra en la API de WhatsApp de la plataforma. Si hoy lo atiende n8n u otro sistema, no lo conectes aquí: dejaría de funcionar allá.</p>
        <p>Mientras la app de Meta no pase la revisión, solo funciona con cuentas que tengan un rol en la app.</p>
      </ConfirmModal>
    </div>
  );
}
